import { create as createTimesync } from 'timesync';
import { socket } from '@/utils/hooks/use-subscription';

const TIMESYNC_CHANNEL = 'timesync';

// Single global timesync instance. Talks to the backend over the existing
// socket.io connection (channel "timesync"). Re-syncs every minute; clock
// drift is slow so this is enough to keep server-relative animations aligned.
const ts = createTimesync({
    server: 'server',
    interval: 60_000,
});

ts.send = (to: string, data: any, timeout: number) => {
    return new Promise<void>((resolve, reject) => {
        const handle = setTimeout(() => reject(new Error('Timesync send timeout')), timeout);
        try {
            socket.emit(TIMESYNC_CHANNEL, data, () => {
                clearTimeout(handle);
                resolve();
            });
        } catch (err) {
            clearTimeout(handle);
            reject(err);
        }
    });
};

// Listeners that fire whenever the offset moves (initial sync + every resync).
// Components mounted mid-roll subscribe to this so they can recompute animation
// durations once the real server-relative clock is known.
const offsetListeners = new Set<(offset: number) => void>();
let lastOffset = 0;
let synced = false;

export function onTimesyncChange(cb: (offset: number) => void): () => void {
    offsetListeners.add(cb);
    return () => offsetListeners.delete(cb);
}

export function isTimesynced(): boolean {
    return synced;
}

if (typeof window !== 'undefined') {
    socket.on(TIMESYNC_CHANNEL, (data: any) => {
        ts.receive('server', data);
    });

    ts.on('change', (offset: number) => {
        lastOffset = offset;
        synced = true;
        offsetListeners.forEach((cb) => cb(offset));

        if (process.env.NEXT_PUBLIC_DEV === 'true') {
            // eslint-disable-next-line no-console
            console.log(`[timesync] offset = ${offset.toFixed(1)}ms`);
        }
    });
}

export const syncedNow = (): number => ts.now();
export default ts;
